/**
 * Excel Export Utility - V172
 * 공통 엑셀 다운로드 유틸리티
 *
 * Features:
 * - 재사용 가능한 엑셀 생성 함수
 * - 컬럼 매핑 및 라벨 커스터마이징
 * - 필터링된 데이터 다운로드 지원
 * - 한글 컬럼명 지원
 * - 날짜/시간 포맷팅 지원
 *
 * Usage:
 * ```tsx
 * import { exportToExcel } from '@/utils/excelExport';
 *
 * // 기본 사용
 * exportToExcel({
 *   data: consults,
 *   columns: CONSULT_EXCEL_COLUMNS,
 *   fileName: '상담이력',
 *   sheetName: '상담목록',
 * });
 *
 * // 필터 정보 포함
 * exportToExcel({
 *   data: consults,
 *   columns: CONSULT_EXCEL_COLUMNS,
 *   fileName: '상담이력',
 *   filterInfo: { period: '2024-01-01 ~ 2024-01-31', status: '콜백 필요' },
 * });
 * ```
 */

import * as XLSX from "xlsx";

/**
 * 엑셀 내보내기용 데이터 타입
 */
export interface ConsultExcelData {
  id: number;
  occurredAt?: string | null;
  customerName?: string | null;
  callerNumber?: string | null;
  type?: string | null;
  status?: string | null;
  callbackStatus?: string | null;
  appointmentStatus?: string | null;
  handler?: string | null;
  durationSeconds?: number | null;
  callDirection?: string | null;
  summary?: string | null;
  priority?: string | null;
}

export interface CustomerExcelData {
  id: number;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  birthDate?: string | null;
  address?: string | null;
  isVip?: boolean | null;
  consultCount?: number | null;
  visitCount?: number | null;
  lastConsultDate?: string | null;
  note?: string | null;
  createdAt?: string | null;
}

export interface AppointmentExcelData {
  id: number;
  customerName?: string | null;
  customerPhone?: string | null;
  appointmentDate?: string | null;
  department?: string | null;
  status?: string | null;
  purpose?: string | null;
  doctorName?: string | null;
}

/**
 * 엑셀 컬럼 정의
 */
export interface ExcelColumn<T> {
  /** 데이터 필드 키 */
  key: keyof T | string;
  /** 엑셀 헤더에 표시될 라벨 */
  label: string;
  /** 컬럼 너비 (문자 수 기준) */
  width?: number;
  /** 값 변환 함수 (날짜 포맷팅, 상태값 한글화 등) */
  transform?: (value: unknown, row: T) => string | number;
}

/**
 * 엑셀 내보내기 옵션
 */
export interface ExportToExcelOptions<T> {
  /** 내보낼 데이터 배열 */
  data: T[];
  /** 컬럼 정의 배열 */
  columns: ExcelColumn<T>[];
  /** 다운로드할 파일명 (확장자 제외) */
  fileName: string;
  /** 시트 이름 (기본: 'Sheet1') */
  sheetName?: string;
  /** 필터 정보 (시트 상단에 표시) */
  filterInfo?: Record<string, string>;
  /** 추가 메타 정보 */
  metadata?: {
    exportedAt?: boolean; // 내보낸 시간 표시 (기본: true)
    totalCount?: boolean; // 총 건수 표시 (기본: true)
  };
}

/**
 * 값을 안전하게 가져오는 헬퍼 함수
 * nested key 지원 (예: 'customer.name')
 */
function getNestedValue(obj: Record<string, unknown>, key: string): unknown {
  return key.split(".").reduce((acc: unknown, part) => {
    if (
      acc &&
      typeof acc === "object" &&
      part in (acc as Record<string, unknown>)
    ) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

/**
 * 현재 날짜를 파일명에 사용할 형식으로 변환
 */
function getDateForFileName(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
}

/**
 * 현재 날짜/시간을 포맷팅
 */
function getFormattedDateTime(): string {
  const now = new Date();
  return now.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * 엑셀 파일로 데이터 내보내기
 */
export function exportToExcel<T extends Record<string, any>>({
  data,
  columns,
  fileName,
  sheetName = "Sheet1",
  filterInfo,
  metadata = { exportedAt: true, totalCount: true },
}: ExportToExcelOptions<T>): void {
  // 워크시트 데이터 준비
  const worksheetData: (string | number)[][] = [];

  // 1. 메타 정보 추가 (필터, 내보낸 시간 등)
  if (filterInfo) {
    Object.entries(filterInfo).forEach(([key, value]) => {
      worksheetData.push([`${key}: ${value}`]);
    });
  }

  if (metadata.exportedAt) {
    worksheetData.push([`내보낸 시간: ${getFormattedDateTime()}`]);
  }

  if (metadata.totalCount) {
    worksheetData.push([`총 ${data.length}건`]);
  }

  // 메타 정보가 있으면 빈 행 추가
  if (worksheetData.length > 0) {
    worksheetData.push([]);
  }

  // 2. 헤더 행 추가
  const headerRow = columns.map((col) => col.label);
  worksheetData.push(headerRow);

  // 3. 데이터 행 추가
  data.forEach((row) => {
    const dataRow = columns.map((col) => {
      const rawValue = getNestedValue(row, col.key as string);

      if (col.transform) {
        return col.transform(rawValue, row);
      }

      // null/undefined 처리
      if (rawValue === null || rawValue === undefined) {
        return "";
      }

      // boolean 처리
      if (typeof rawValue === "boolean") {
        return rawValue ? "Y" : "N";
      }

      return rawValue;
    });
    worksheetData.push(dataRow);
  });

  // 4. 워크시트 생성
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // 5. 컬럼 너비 설정
  const colWidths = columns.map((col) => ({
    wch: col.width || Math.max(col.label.length * 2, 10),
  }));
  worksheet["!cols"] = colWidths;

  // 6. 워크북 생성 및 시트 추가
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // 7. 파일 다운로드
  const fullFileName = `${fileName}_${getDateForFileName()}.xlsx`;
  XLSX.writeFile(workbook, fullFileName);
}

/**
 * 상담 이력용 컬럼 정의
 */
export const CONSULT_EXCEL_COLUMNS: ExcelColumn<ConsultExcelData>[] = [
  {
    key: "id",
    label: "ID",
    width: 8,
  },
  {
    key: "occurredAt",
    label: "상담일시",
    width: 18,
    transform: (value) => {
      if (!value) return "";
      const date = new Date(value);
      return date.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  {
    key: "customerName",
    label: "고객명",
    width: 12,
    transform: (value) => value || "미지정",
  },
  {
    key: "callerNumber",
    label: "연락처",
    width: 15,
    // V189: 전화번호를 텍스트로 유지 (엑셀에서 숫자 변환 방지)
    transform: (value) => (value ? `\t${value}` : ""),
  },
  {
    key: "type",
    label: "상담분류",
    width: 12,
    transform: (value) => {
      const typeMap: Record<string, string> = {
        RESERVATION: "예약문의",
        COMPLAINT: "불만/클레임",
        GENERAL: "일반문의",
        LOCATION_INQUIRY: "위치안내",
        OTHER: "기타",
      };
      return typeMap[value] || value || "";
    },
  },
  {
    key: "status",
    label: "처리상태",
    width: 14,
    transform: (value) => {
      const statusMap: Record<string, string> = {
        PENDING: "대기",
        FAILED: "실패",
        IN_PROGRESS: "처리 중",
        CALLBACK_NEEDED: "콜백 필요",
        CALLBACK_COMPLETED: "콜백 완료",
        OUTBOUND_NEEDED: "아웃바운드 필요",
        OUTBOUND_COMPLETED: "아웃바운드 완료",
        COMPLETED: "처리 완료",
      };
      return statusMap[value] || value || "";
    },
  },
  {
    key: "callbackStatus",
    label: "콜백상태",
    width: 12,
    transform: (value) => {
      const callbackMap: Record<string, string> = {
        NOT_NEEDED: "콜백 불필요",
        PENDING: "콜백 대기",
        ASSIGNED: "담당 배정",
        IN_PROGRESS: "진행 중",
        COMPLETED: "완료",
        CANCELLED: "취소",
      };
      return callbackMap[value] || value || "";
    },
  },
  {
    key: "appointmentStatus",
    label: "예약상태",
    width: 12,
    transform: (value) => {
      const appointmentMap: Record<string, string> = {
        NOT_REQUESTED: "",
        REQUESTED: "예약 요청",
        CHECKING: "확인 중",
        AVAILABLE: "예약 가능",
        NOT_AVAILABLE: "예약 불가",
        CONFIRMED: "예약 확정",
        MODIFICATION_REQUESTED: "변경 요청",
        CANCELLATION_REQUESTED: "취소 요청",
        SMS_SENT: "문자 발송",
        COMPLETED: "예약 완료",
        CANCELLED: "예약 취소",
        NO_SHOW: "노쇼",
        PENDING: "대기",
      };
      return appointmentMap[value] || value || "";
    },
  },
  {
    key: "handler",
    label: "담당자",
    width: 12,
    transform: (value) => value || "AI 분석",
  },
  {
    key: "durationSeconds",
    label: "통화시간",
    width: 10,
    transform: (value) => {
      if (!value) return "";
      const mins = Math.floor(value / 60);
      const secs = value % 60;
      return `${mins}분 ${secs}초`;
    },
  },
  {
    key: "callDirection",
    label: "방향",
    width: 8,
    transform: (value) => {
      return value === "INBOUND" ? "수신" : value === "OUTBOUND" ? "발신" : "";
    },
  },
  {
    key: "summary",
    label: "상담요약",
    width: 40,
    transform: (value) => value || "",
  },
  {
    key: "priority",
    label: "우선순위",
    width: 10,
    transform: (value) => {
      const priorityMap: Record<string, string> = {
        CRITICAL: "긴급",
        HIGH: "보통",
        MEDIUM: "보통",
        LOW: "보통",
      };
      return priorityMap[value] || value || "";
    },
  },
];

/**
 * 고객 관리용 컬럼 정의
 */
export const CUSTOMER_EXCEL_COLUMNS: ExcelColumn<CustomerExcelData>[] = [
  {
    key: "id",
    label: "ID",
    width: 8,
  },
  {
    key: "name",
    label: "고객명",
    width: 12,
  },
  {
    key: "phone",
    label: "연락처",
    width: 15,
    // V189: 전화번호를 텍스트로 유지 (엑셀에서 숫자 변환 방지)
    transform: (value) => (value ? `\t${value}` : ""),
  },
  {
    key: "email",
    label: "이메일",
    width: 25,
  },
  {
    key: "birthDate",
    label: "생년월일",
    width: 12,
    transform: (value) => {
      if (!value) return "";
      const date = new Date(value);
      return date.toLocaleDateString("ko-KR");
    },
  },
  {
    key: "address",
    label: "주소",
    width: 30,
  },
  {
    key: "isVip",
    label: "VIP",
    width: 6,
    transform: (value) => (value ? "Y" : ""),
  },
  {
    key: "consultCount",
    label: "상담횟수",
    width: 10,
  },
  {
    key: "visitCount",
    label: "방문횟수",
    width: 10,
  },
  {
    key: "lastConsultDate",
    label: "최근상담일",
    width: 12,
    transform: (value) => {
      if (!value) return "";
      const date = new Date(value);
      return date.toLocaleDateString("ko-KR");
    },
  },
  {
    key: "note",
    label: "메모",
    width: 40,
  },
  {
    key: "createdAt",
    label: "등록일",
    width: 12,
    transform: (value) => {
      if (!value) return "";
      const date = new Date(value);
      return date.toLocaleDateString("ko-KR");
    },
  },
];

/**
 * 예약 관리용 컬럼 정의
 */
export const APPOINTMENT_EXCEL_COLUMNS: ExcelColumn<AppointmentExcelData>[] = [
  {
    key: "id",
    label: "ID",
    width: 8,
  },
  {
    key: "customerName",
    label: "고객명",
    width: 12,
  },
  {
    key: "customerPhone",
    label: "연락처",
    width: 15,
    // V189: 전화번호를 텍스트로 유지 (엑셀에서 숫자 변환 방지)
    transform: (value) => (value ? `\t${value}` : ""),
  },
  {
    key: "appointmentDate",
    label: "예약일시",
    width: 18,
    transform: (value) => {
      if (!value) return "";
      const date = new Date(value);
      return date.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  {
    key: "department",
    label: "진료과목",
    width: 12,
  },
  {
    key: "status",
    label: "예약상태",
    width: 12,
    transform: (value) => {
      const statusMap: Record<string, string> = {
        REQUESTED: "요청",
        CHECKING: "확인중",
        CONFIRMED: "확정",
        CANCELLED: "취소",
        COMPLETED: "완료",
        NO_SHOW: "노쇼",
      };
      return statusMap[value] || value || "";
    },
  },
  {
    key: "purpose",
    label: "예약내용",
    width: 30,
  },
  {
    key: "doctorName",
    label: "담당의",
    width: 10,
  },
];

export default exportToExcel;
